import { useState } from "react";
import useScrollAnimation from "@/hooks/useScrollAnimation";
import { servicesData } from "@/mocks/homeData";

export default function ServicesSection() {
  const [activeTab, setActiveTab] = useState("technology");
  const activeService = servicesData.tabs.find((t) => t.id === activeTab);
  const { ref, isVisible } = useScrollAnimation(0.08);

  const handleNavClick = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="services" className="w-full py-16 md:py-24 bg-background-100">
      <div
        ref={ref}
        className={`w-full px-4 md:px-6 lg:px-10 transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <span className="text-xs md:text-sm font-semibold text-primary-500 uppercase tracking-widest">
            {servicesData.label}
          </span>
          <h2 className="font-heading font-bold text-xl md:text-3xl lg:text-4xl text-foreground-950 mt-3 mb-4 leading-tight">
            {servicesData.headline}
          </h2>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed max-w-2xl mx-auto">
            {servicesData.subheadline}
          </p>
        </div>

        <div className="flex justify-center mb-10 md:mb-12">
          <div className="inline-flex p-1 rounded-full bg-background-200/70 border border-background-300/60">
            {servicesData.tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 md:px-6 py-2.5 text-sm font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? "bg-primary-500 text-background-50 shadow-sm"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {activeService && (
          <div className="animate-fadeIn">
            <div className="max-w-3xl mx-auto text-center mb-10">
              <h3 className="font-heading font-semibold text-lg md:text-2xl text-foreground-950 mb-3">
                {activeService.title}
              </h3>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {activeService.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-6xl mx-auto">
              {activeService.offerings.map((item, index) => (
                <div
                  key={item.title}
                  className="group bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70 hover:border-primary-300 transition-all hover:-translate-y-1"
                  style={{ animationDelay: `${index * 80}ms` }}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-10 h-10 md:w-11 md:h-11 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                      <i className={`${item.icon} text-lg md:text-xl text-primary-500`} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-heading font-semibold text-base md:text-lg text-foreground-950 mb-1.5">
                        {item.title}
                      </h4>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                  {item.highlights && (
                    <div className="flex flex-wrap gap-2 pl-14 md:pl-[60px]">
                      {item.highlights.map((h) => (
                        <span
                          key={h}
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-700 bg-primary-50/80 px-2.5 py-1 rounded-full"
                        >
                          <i className="ri-check-line text-xs" />
                          {h}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <button
                onClick={() => handleNavClick("#contact")}
                className="px-6 py-3 text-sm font-semibold rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Discuss Your Requirements
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}